import clamp from 'lodash.clamp';
import { Rectangle } from 'pixi.js';

import { MAX_CAM_ZOOM, MIN_CAM_ZOOM } from 'js/config/defaults';
import { getPositionAdjustmentToFitInBounds } from 'js/sagas/sagaHelpers/getPositionAdjustmentToFitInBounds';
import { ViewportRectAndCenter } from 'js/types';

/**
 * Calculates what the camera position should be keeping it fitted to the viewport, but adjusting it
 * so that scales stay withing scale bounds and positions stay within canvas bounds
 */
export const getAdjustedNewCameraRect = (
  viewportRectAndCenter: ViewportRectAndCenter,
  canvasSize: { width: number; height: number },
  canvasBounds: Rectangle
) => {
  const { viewportRectangle, viewportCenter } = viewportRectAndCenter;

  const cameraAspect = canvasSize.width / canvasSize.height;
  const newCamBounds = new Rectangle(
    0,
    0,
    Math.min(viewportRectangle.width, canvasBounds.width),
    Math.min(viewportRectangle.height, canvasBounds.height)
  );
  const viewportAspect = newCamBounds.width / newCamBounds.height;
  let newFittedScale;
  // Determine the scaling factor based on aspect ratios
  if (cameraAspect > viewportAspect) {
    // Fit the target rectangle horizontally within the container
    newFittedScale = newCamBounds.width / canvasSize.width;
  } else {
    // Fit the target rectangle vertically within the container
    newFittedScale = newCamBounds.height / canvasSize.height;
  }
  newFittedScale = clamp(1 / newFittedScale, MIN_CAM_ZOOM, MAX_CAM_ZOOM);

  const targetCameraRect = new Rectangle(
    viewportCenter.x - canvasSize.width / newFittedScale / 2,
    viewportCenter.y - canvasSize.height / newFittedScale / 2,
    canvasSize.width / newFittedScale,
    canvasSize.height / newFittedScale
  );

  const { xAdjust, yAdjust } = getPositionAdjustmentToFitInBounds(targetCameraRect, canvasBounds);
  const adjustedCameraRectangle = new Rectangle(
    targetCameraRect.x + xAdjust,
    targetCameraRect.y + yAdjust,
    targetCameraRect.width,
    targetCameraRect.height
  );

  return {
    scale: newFittedScale,
    adjustedCameraRectangle
  };
};

type GetAdjustedCameraRectForBoundsOptions = {
  canvasSize: { width: number; height: number };
  bounds: Rectangle;
};

const ELEMENT_CAMERA_PADDING = 100; // Padding around the elements when fitting the camera
export const getAdjustedCameraRectForBounds = ({ bounds, canvasSize }: GetAdjustedCameraRectForBoundsOptions) => {
  const cameraAspect = canvasSize.width / canvasSize.height;
  const boundsAspect = bounds.width / bounds.height;

  // Calculate the tight scale needed to fit bounds exactly
  let tightScale;
  if (cameraAspect > boundsAspect) {
    // Fit the bounds vertically within the canvas
    tightScale = canvasSize.height / bounds.height;
  } else {
    // Fit the bounds horizontally within the canvas
    tightScale = canvasSize.width / bounds.width;
  }

  // Calculate padding in world space based on the tight scale
  const paddingInWorldSpace = ELEMENT_CAMERA_PADDING / tightScale;

  // Apply padding to bounds
  const paddedBounds = new Rectangle(
    bounds.x - paddingInWorldSpace,
    bounds.y - paddingInWorldSpace,
    bounds.width + paddingInWorldSpace * 2,
    bounds.height + paddingInWorldSpace * 2
  );

  const paddedBoundsAspect = paddedBounds.width / paddedBounds.height;

  // Determine the final scaling factor based on padded bounds
  let scale;
  if (cameraAspect > paddedBoundsAspect) {
    // Fit the padded bounds vertically within the canvas
    scale = canvasSize.height / paddedBounds.height;
  } else {
    // Fit the padded bounds horizontally within the canvas
    scale = canvasSize.width / paddedBounds.width;
  }

  // Clamp the scale to valid zoom bounds
  scale = clamp(scale, MIN_CAM_ZOOM, MAX_CAM_ZOOM);

  // Calculate the center of the padded bounds
  const boundsCenter = {
    x: paddedBounds.x + paddedBounds.width / 2,
    y: paddedBounds.y + paddedBounds.height / 2
  };

  // Create the camera rectangle centered on the padded bounds
  const adjustedCameraRectangle = new Rectangle(
    boundsCenter.x - canvasSize.width / scale / 2,
    boundsCenter.y - canvasSize.height / scale / 2,
    canvasSize.width / scale,
    canvasSize.height / scale
  );

  return {
    scale,
    adjustedCameraRectangle
  };
};
